import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { altDataUpgradeDialogState, chatMinimizedState, chatModalState, themeState, userDataState } from "@/lib/store";
import { classNames } from "@/lib/utils";
import { useTradingIdeasAccess } from "src/hooks/useTradingIdeasAccess";
import { popularChips, sideMenuGroups, myOrtexGroup, SideMenuGroup, SideMenuItem, SideMenuSubLink } from "./menuGroups";
import styles from "./MenuContent.module.scss";

interface FlyoutState {
    key: string;
    top: number;
    left: number;
    items: SideMenuSubLink[];
}

const stripQuery = (path: string) => (path || "").split("?")[0];

interface MenuContentProps {
    // Close the containing drawer after a navigation.
    onNavigate?: () => void;
}

const MenuContent = ({ onNavigate }: MenuContentProps) => {
    const [flyout, setFlyout] = useState<FlyoutState | null>(null);
    // Touch / narrow screens have no hover and no room to the right of the
    // panel, so parents expand inline (accordion) instead of opening a flyout.
    const [inline, setInline] = useState(false);
    const [expanded, setExpanded] = useState<string | null>(null);
    useEffect(() => {
        const mq = window.matchMedia("(pointer: coarse), (max-width: 900px)");
        const update = () => setInline(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    const theme = useRecoilValue(themeState);
    const isLight = theme === "light";
    const router = useRouter();
    const currentPath = stripQuery(router.asPath);

    const user = useRecoilValue(userDataState);
    const setChatModal = useSetRecoilState(chatModalState);
    const setChatMinimized = useSetRecoilState(chatMinimizedState);
    const setAltDataUpgradeOpen = useSetRecoilState(altDataUpgradeDialogState);
    // Alt Data entitlement (Advanced and above), from the user payload.
    const canSeeAltData = !!(user as { alt_data?: boolean } | null)?.alt_data;

    // Two independent gates, both declared on the config: the Options Platform
    // pair comes off the user record, Trading Ideas only from a backend probe.
    const canSeeOptionsPlatform = !!(user?.options_platform_subscriber || user?.new_options_ui);
    const { access: tradingIdeasAccess } = useTradingIdeasAccess();

    const visible = (item: SideMenuItem) => tradingIdeasAccess === true || !item.requiresTradingIdeasAccess;
    const filterSubmenu = (item: SideMenuItem): SideMenuItem =>
        item.submenu ? { ...item, submenu: item.submenu.filter((s) => canSeeOptionsPlatform || !s.requiresOptionsPlatform) } : item;

    const chips = useMemo(() => popularChips.filter(visible).map(filterSubmenu), [canSeeOptionsPlatform, tradingIdeasAccess]);
    const groups: SideMenuGroup[] = useMemo(
        () => sideMenuGroups.map((group) => ({ ...group, items: group.items.filter(visible).map(filterSubmenu) })),
        [canSeeOptionsPlatform, tradingIdeasAccess]
    );

    // ORTEX AI has no route — the menu opens the chat modal the header owns.
    const openChat = () => {
        onNavigate?.();
        if (user?.subscription_plan === "ORTEX Visitor") {
            window.location.href = `${window.location.origin}/login`;
            return;
        }
        setChatModal({ open: true, initialMessage: undefined, startingModel: undefined, chatModel: undefined });
        setChatMinimized(false);
    };

    const flyoutTimer = useRef<NodeJS.Timeout>();
    const panelRef = useRef<HTMLDivElement>(null);

    const isActive = (item: SideMenuItem) => {
        if (item.link && stripQuery(item.link) === currentPath) return true;
        return Boolean(item.submenu?.some((sub) => stripQuery(sub.link) === currentPath));
    };

    const openFlyout = (target: HTMLElement, item: SideMenuItem) => {
        clearTimeout(flyoutTimer.current);
        if (!item.submenu) {
            setFlyout(null);
            return;
        }
        const rect = target.getBoundingClientRect();
        const panelRight = panelRef.current?.getBoundingClientRect().right ?? rect.right;
        const height = item.submenu.length * 30 + 8;
        const top = Math.max(64, Math.min(rect.top, window.innerHeight - height - 8));
        setFlyout({ key: item.text, top, left: panelRight + 6, items: item.submenu });
    };

    const scheduleCloseFlyout = () => {
        clearTimeout(flyoutTimer.current);
        flyoutTimer.current = setTimeout(() => setFlyout(null), 150);
    };

    const cancelCloseFlyout = () => clearTimeout(flyoutTimer.current);

    const renderRow = (item: SideMenuItem) => {
        const active = isActive(item);
        const hasFlyout = Boolean(item.submenu);
        const flyoutOpen = flyout?.key === item.text;
        const rowClass = classNames(styles.row, active && styles.active, flyoutOpen && styles.flyoutOpen);

        const content = (
            <>
                <div className={classNames(item.icon, styles.rowIcon)} />
                <span className={styles.rowLabel}>{item.text}</span>
                {item.tag && <span className={styles.rowNew}>{item.tag}</span>}
                {hasFlyout && <i className={classNames("fal fa-chevron-right", styles.rowChevron)} />}
            </>
        );

        // Off-site (MCP): next/link can't route it, and leaving the app is
        // announced rather than left to the icon (1.1.1).
        if (item.external) {
            return (
                <a
                    className={rowClass}
                    role="menuitem"
                    key={item.text}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onNavigate}
                    onMouseEnter={scheduleCloseFlyout}
                    onFocus={scheduleCloseFlyout}
                >
                    <div className={classNames(item.icon, styles.rowIcon)} />
                    <span className={styles.rowLabel}>{item.text}</span>
                    <span className={styles.srOnly}> (opens in a new tab)</span>
                    <i className={classNames("fal fa-arrow-up-right-from-square", styles.rowExternal)} aria-hidden="true" />
                </a>
            );
        }

        // Gated page: entitled users navigate, everyone else gets the upgrade dialog.
        if (item.requiresAltData && !canSeeAltData) {
            return (
                <button
                    type="button"
                    className={rowClass}
                    role="menuitem"
                    key={item.text}
                    onClick={() => {
                        onNavigate?.();
                        setAltDataUpgradeOpen(true);
                    }}
                    onMouseEnter={scheduleCloseFlyout}
                    onFocus={scheduleCloseFlyout}
                >
                    {content}
                </button>
            );
        }

        if (item.action === "chat") {
            return (
                <button type="button" className={rowClass} role="menuitem" key={item.text} onClick={openChat} onMouseEnter={scheduleCloseFlyout} onFocus={scheduleCloseFlyout}>
                    {content}
                </button>
            );
        }

        // Touch / narrow: the parent row toggles an inline sub-list.
        if (hasFlyout && inline) {
            const open = expanded === item.text;
            return (
                <div key={item.text} className={styles.inlineGroup}>
                    <button type="button" className={classNames(rowClass, open && styles.flyoutOpen)} role="menuitem" aria-expanded={open} onClick={() => setExpanded(open ? null : item.text)}>
                        <div className={classNames(item.icon, styles.rowIcon)} />
                        <span className={styles.rowLabel}>{item.text}</span>
                        {item.tag && <span className={styles.rowNew}>{item.tag}</span>}
                        <i className={classNames("fal fa-chevron-right", styles.rowChevron, open && styles.rowChevronOpen)} />
                    </button>
                    {open && (
                        <div className={styles.subList}>
                            {item.submenu?.map((sub) => {
                                const subActive = stripQuery(sub.link) === currentPath;
                                return (
                                    <Link href={sub.link} passHref key={sub.text}>
                                        <a className={classNames(styles.row, styles.subRow, subActive && styles.active)} role="menuitem" onClick={onNavigate}>
                                            <div className={classNames(sub.icon, styles.rowIcon)} />
                                            <span className={styles.rowLabel}>{sub.text}</span>
                                        </a>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // Parent with submenu: opens the flyout on hover; click navigates to the
        // first submenu item (matches the previous desktop behaviour).
        if (hasFlyout) {
            const firstLink = item.submenu?.[0]?.link ?? "#";
            return (
                <Link href={firstLink} passHref key={item.text}>
                    <a
                        className={rowClass}
                        role="menuitem"
                        tabIndex={0}
                        aria-haspopup="true"
                        aria-expanded={flyoutOpen}
                        onClick={onNavigate}
                        onMouseEnter={(e) => openFlyout(e.currentTarget, item)}
                        onMouseLeave={scheduleCloseFlyout}
                        onFocus={(e) => openFlyout(e.currentTarget, item)}
                        onKeyDown={(e) => {
                            if (e.key === "ArrowRight") openFlyout(e.currentTarget, item);
                            if (e.key === "ArrowLeft" || e.key === "Escape") setFlyout(null);
                        }}
                    >
                        {content}
                    </a>
                </Link>
            );
        }

        return (
            <Link href={item.link ?? "#"} passHref key={item.text}>
                <a className={rowClass} role="menuitem" tabIndex={0} onClick={onNavigate} onMouseEnter={scheduleCloseFlyout} onFocus={scheduleCloseFlyout}>
                    {content}
                </a>
            </Link>
        );
    };

    // A POPULAR chip. Chips with a submenu open the flyout on hover/focus (and
    // navigate to their own link on click); the rest are plain links.
    const renderChip = (chip: SideMenuItem) => {
        const hasFlyout = Boolean(chip.submenu);
        const flyoutOpen = flyout?.key === chip.text;
        if (hasFlyout && inline) {
            const open = expanded === chip.text;
            return (
                <div key={chip.text} className={styles.inlineGroup}>
                    <button type="button" className={classNames(styles.chip, open && styles.chipOpen)} aria-expanded={open} onClick={() => setExpanded(open ? null : chip.text)}>
                        <div className={chip.icon} />
                        <span className={styles.chipLabel}>{chip.text}</span>
                        {chip.tag && <span className={styles.chipNew}>{chip.tag}</span>}
                        <i className={classNames("fal fa-chevron-right", styles.chipChevron, open && styles.rowChevronOpen)} />
                    </button>
                    {open && (
                        <div className={styles.subList}>
                            {chip.submenu?.map((sub) => {
                                const subActive = stripQuery(sub.link) === currentPath;
                                return (
                                    <Link href={sub.link} passHref key={sub.text}>
                                        <a className={classNames(styles.row, styles.subRow, subActive && styles.active)} role="menuitem" onClick={onNavigate}>
                                            <div className={classNames(sub.icon, styles.rowIcon)} />
                                            <span className={styles.rowLabel}>{sub.text}</span>
                                        </a>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }
        return (
            <Link href={chip.link ?? "#"} passHref key={chip.text}>
                <a
                    className={classNames(styles.chip, flyoutOpen && styles.chipOpen)}
                    onClick={onNavigate}
                    aria-haspopup={hasFlyout || undefined}
                    aria-expanded={hasFlyout ? flyoutOpen : undefined}
                    onMouseEnter={hasFlyout ? (e) => openFlyout(e.currentTarget, chip) : scheduleCloseFlyout}
                    onMouseLeave={hasFlyout ? scheduleCloseFlyout : undefined}
                    onFocus={hasFlyout ? (e) => openFlyout(e.currentTarget, chip) : scheduleCloseFlyout}
                >
                    <div className={chip.icon} />
                    <span className={styles.chipLabel}>{chip.text}</span>
                    {chip.tag && <span className={styles.chipNew}>{chip.tag}</span>}
                    {hasFlyout && <i className={classNames("fal fa-chevron-right", styles.chipChevron)} />}
                </a>
            </Link>
        );
    };

    return (
        <div ref={panelRef} className={classNames(styles.panel, isLight && styles.light)} role="menu">
            {/* Brand row: logo home link + close */}
            <div className={styles.brand}>
                <Link href="/" passHref>
                    <a onClick={onNavigate} aria-label="ORTEX home">
                        <Image unoptimized src="/assets/ortex-logo-h.png" alt="ORTEX" width={105} height={22} priority />
                    </a>
                </Link>
                <button type="button" className={styles.closeBtn} onClick={onNavigate} aria-label="Close menu">
                    <i className="fal fa-xmark" aria-hidden="true" />
                </button>
            </div>

            {/* POPULAR chips */}
            <div className={styles.popular}>
                <div className={styles.popularLabel}>Popular</div>
                <div className={styles.chips}>{chips.map(renderChip)}</div>
            </div>

            {/* Catalog groups */}
            <div className={styles.scrollArea}>
                {groups.map((group, gi) => (
                    <div key={group.label}>
                        <div className={classNames(styles.groupLabel, gi > 0 && styles.groupDivider)}>{group.label}</div>
                        {group.items.map(renderRow)}
                    </div>
                ))}
            </div>

            {/* MY ORTEX (bottom-pinned) */}
            <div className={styles.myOrtex}>
                <div className={styles.groupLabel}>{myOrtexGroup.label}</div>
                {myOrtexGroup.items.map(renderRow)}
            </div>

            {flyout &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        className={classNames(styles.flyout, isLight && styles.light)}
                        style={{ top: flyout.top, left: flyout.left }}
                        role="menu"
                        onMouseEnter={cancelCloseFlyout}
                        onMouseLeave={scheduleCloseFlyout}
                    >
                        {flyout.items.map((sub) => {
                            const active = stripQuery(sub.link) === currentPath;
                            return (
                                <Link href={sub.link} passHref key={sub.text}>
                                    <a className={classNames(styles.row, active && styles.active)} role="menuitem" onClick={onNavigate}>
                                        <div className={classNames(sub.icon, styles.rowIcon)} />
                                        <span className={styles.rowLabel}>{sub.text}</span>
                                    </a>
                                </Link>
                            );
                        })}
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default MenuContent;
